import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Chip,
  Divider,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔹 Dialog State for “+ more”
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogPermissions, setDialogPermissions] = useState([]);

  // ---------------- Helper: Convert to Full Image URL ----------------
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${IMAGE_BASE_URL}${imagePath}`; // ✅ use IMAGE_BASE_URL
  };

  // ---------------- Token Check ----------------
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // ---------------- Fetch User Detail ----------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          navigate("/login", { replace: true });
          return;
        }

        const response = await axios.get(ENDPOINT.USER_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          setUser(response.data.user);
        } else {
          toast.error("Failed to fetch user details");
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        toast.error("Failed to load user detail");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate]);

  // ---------------- Dialog Handlers ----------------
  const handleOpenDialog = (groupName, permissions) => {
    setDialogTitle(groupName);
    setDialogPermissions(permissions);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogPermissions([]);
  };

  // ---------------- UI ----------------
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center" color="text.secondary">
          User not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/users/list")}
            >
              Back
            </Button>
            <Typography variant="h5" fontWeight={600}>
              👤 User Detail
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Basic Info */}
        <Stack direction="row" spacing={3} alignItems="center">
          <Avatar
            sx={{ width: 80, height: 80, bgcolor: "primary.main" }}
            src={getProfileImageUrl(user.profile_image)} // ✅ fixed image URL
          >
            {!user.profile_image && user.username?.charAt(0).toUpperCase()}
          </Avatar>

          <Box>
            <Typography variant="h6" fontWeight={600}>
              {user.username}
            </Typography>
            <Typography color="text.secondary">{user.email}</Typography>
            <Typography color="text.secondary">{user.phone || "-"}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Groups Section */}
        <Box>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Groups
          </Typography>
          {user.groups?.length > 0 ? (
            user.groups.map((group, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, backgroundColor: "#f9fafc" }}>
                <Typography fontWeight={600}>{group.name || group}</Typography>
                {group.permissions && (
                  <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                    {group.permissions.slice(0, 10).map((perm, i) => (
                      <Chip
                        key={i}
                        label={perm}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                    {group.permissions.length > 10 && (
                      <Chip
                        label={`+${group.permissions.length - 10} more`}
                        size="small"
                        color="secondary"
                        onClick={() =>
                          handleOpenDialog(group.name, group.permissions)
                        }
                        sx={{ cursor: "pointer" }}
                      />
                    )}
                  </Stack>
                )}
              </Paper>
            ))
          ) : (
            <Typography color="text.secondary">No groups assigned</Typography>
          )}
        </Box>
      </Paper>

      {/* Dialog for full permission list */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogTitle} - All Permissions</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {dialogPermissions.map((perm, i) => (
              <Chip key={i} label={perm} color="primary" variant="outlined" />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetail;
